<template>
	<div id="search-filters">
		<div class="filter-header" @click="handleFilterDrawer()">
			<i class="icon" :class="expanded ? 'icon-down-carrot' : 'icon-right-carrot'" />
			<span>Filter by:</span>
			<span class="filter-indicator">{{ objectivesIndicator }}, {{ assetTypesIndicator }}</span>
		</div>
		<div
			class="filter-drawer-body"
			:class="expanded ? 'filter-drawer-expanded' : 'filter-drawer-collapsed'"
		>
			<div id="objectives-container">
				<label>
					<span class="individual-only">Fund</span>Objective
				</label>
				<div id="objectives-select-container" class="clearfix">
					<multi-select
						v-model="chosenObjectives"
						:options="objectivesList"
						placeholder="All Objectives"
					/>
				</div>
			</div>
			<div id="operator-container">
				<span>OR</span>
			</div>
			<div id="asset-types-container">
				<label>Asset Type</label>
				<div id="asset-select-container" class="clearfix">
					<multi-select
						v-model="chosenAssetTypes"
						:options="assetTypeList"
						placeholder="All Asset Types"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch, defineModel, defineEmits, defineOptions, defineProps } from 'vue';
import MultiSelect from '../../components/global/MultiSelect.vue';

defineOptions({
	name: 'SearchFilters'
});

const props = defineProps({
	widgetData: {
		type: Object,
		default: () => ({
			objectives: [],
			assetTypes: []
		})
	}
});


/*
	Defines models for the assetTypes and Objective. These are updated in the multiSelect component and the values, when changed, are emitted to the parent component of SearchFilters.
	(A ref and custom emit event in the child component could be used, but this has that set up already. Despite not altering the values in this component, its less code. Perhaps its better to be more explicit by not using models but if another develop wants to make that decision in the future they can)

	the first argument of defineModel is the name of the model, this also sets the emit event in the child component (handlded under the hood)
*/
const emit = defineEmits(['updateFilters']);
const chosenAssetTypes = defineModel('chosenAssetTypes', { default: [] });
const chosenObjectives = defineModel('chosenObjectives', { default: [] });
watch([() => chosenObjectives.value, () => chosenAssetTypes.value], () => {
	emit('updateFilters', {
		objectives: chosenObjectives,
		assetTypes: chosenAssetTypes
	});
}, {
	deep: true
});

const objectivesIndicator = computed(() => {
	return chosenObjectives.value.length
		? chosenObjectives.value.join(', ')
		: 'All Objectives';
});

const assetTypesIndicator = computed(() => {
	return chosenAssetTypes.value.length
		? chosenAssetTypes.value.join(', ')
		: 'All Asset Types';
});


const objectivesList = computed(() => {
	return props.widgetData.objectives;
});
const assetTypeList = computed(() => props.widgetData.assetTypes);

const expanded = ref(false);
function handleFilterDrawer() {
	expanded.value = !expanded.value;
}
</script>
<style lang="less">
@import '~_css_/common.less';
#search-filters {
	.relative {
		position: relative;
	}
	margin-bottom: 20px;
	.blue-arrow-down {
		background-image: url('../../assets/img/DROPDOWN_ARROW_icon_16px.png');
	}
	.blue-arrow-right {
		background-image: url('../../assets/img/blue-arrow-right.png');
	}
	.filter-header {
		background-color: rgb(203, 234, 248);
		font-weight: bold;
		min-height: 30px;
		cursor: pointer;
		padding: 5px;
		> *::after {
			content: ' ';
		}
		.icon {
			font-size: 9px;
			line-height: 15px;
			color: @brand-primary;
			padding-left: 5px;
		}
		span {
			font-family: 'AvenirNextLTW01-Regular', Arial, sans-serif;
			line-height: 20px;
			margin-bottom: 0px;
			min-height: 30px;
			padding: 4px 0px;
			&.filter-indicator {
				font-weight: normal;
				text-transform: capitalize;
			}
		}
		.direction-marker {
			display: inline-block;
			width: 16px;
			height: 16px;
			background-repeat: no-repeat;
			background-position: top left;
		}
	}
	.filter-drawer-body {
		background-color: #e5f4fb;
		padding: 10px 24px;
		min-height: 100px;
		&.filter-drawer-expanded {
			display: flex;
		}
		&.filter-drawer-collapsed {
			display: none;
		}
		.placeholder {
			color: #999;
		}
		#objectives-container {
			.individual-only::after {
				content: ' ';
			}
		}
		#objectives-container,
		#asset-types-container {
			height: 100%;
			display: inline-block;
			label {
				font-size: 14px;
				color: #333;
				font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
			}
		}
		#operator-container {
			display: inline-block;
			font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
			font-weight: bold;
			margin: 23px 20px 0px;
			padding-top: 5px;
			height: 35px;
		}
		@media (max-width: 768px) {
			flex-wrap: wrap;
			padding: 15px 10px;
			#operator-container {
				flex-basis: 100%;
				margin: 5px 0 0 0;
			}
			#asset-types-container,
			#objectives-container,
			.chosen-container {
				width: 100% !important;
				.search-choice {
					display: block;
				}
			}
		}
	}
}
</style>